import type { FastDriveScanInput, FastDriveScanResult, ScanStatus } from './fast-drive-scan.js';

/**
 * The visual gate: an annotated context crop is worth its screenshot cost ONLY
 * where text alone is weak — the `ambiguous` ranked set, a coordinate-probe
 * (visual confirmation of what the pixel hit), or an explicit `visualize`. It is
 * deliberately NEVER emitted on a text-query `ready` — that's the common
 * confident case whose whole value is being cheap and image-free.
 */
export function shouldRenderScanVisual(input: FastDriveScanInput, status: ScanStatus): boolean {
  return input.visualize === true || status === 'ambiguous' || !!input.at;
}

/**
 * The boxes to outline in the crop (viewport coords, `{x,y,width,height}`). The
 * confident target when there is one, else the ranked candidate set; for a
 * coordinate-probe that resolved nothing, a small marker around the point so the
 * agent still SEES where it pointed (an empty / covered region).
 */
export function scanHighlightBoxes(
  input: FastDriveScanInput,
  result: FastDriveScanResult
): Array<{ x: number; y: number; width: number; height: number }> {
  const src = result.target ? [result.target] : result.candidates;
  const boxes = src
    .map((c) => c.bbox)
    .filter((b): b is NonNullable<typeof b> => !!b)
    .map((b) => ({ x: b.x, y: b.y, width: b.w, height: b.h }));
  if (boxes.length === 0 && input.at) {
    boxes.push({ x: input.at.x - 40, y: input.at.y - 40, width: 80, height: 80 });
  }
  return boxes;
}
